import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { CheckCircle2, AlertCircle } from '../lib/icons';
import { shadows } from '../lib/styles';

export interface ClarifyQuestion {
  id: string;
  question: string;
  answer: string;
}

interface ClarifyQuestionsFormProps {
  questions: ClarifyQuestion[];
  onSubmit: (answers: ClarifyQuestion[]) => Promise<void>;
  disabled?: boolean;
}

export const ClarifyQuestionsForm: React.FC<ClarifyQuestionsFormProps> = ({
  questions,
  onSubmit,
  disabled = false,
}) => {
  const [answers, setAnswers] = useState<ClarifyQuestion[]>(questions);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAnswers(questions);
  }, [questions]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => 
      prev.map(q => q.id === questionId ? { ...q, answer } : q)
    );
  };

  const handleSubmit = async () => {
    // Validate all questions have answers
    const unanswered = answers.filter(q => !q.answer.trim());
    if (unanswered.length > 0) {
      setError(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit(answers);
    } catch (err: any) {
      setError(err.message || 'Failed to submit answers');
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = answers.every(q => q.answer.trim().length > 0);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <AlertCircle size={16} color="#2563EB" />
        <Text style={styles.headerText}>
          Please answer the following questions to help us serve you better:
        </Text>
      </View>

      <View style={styles.questionsContainer}>
        {answers.map((question, index) => (
          <View key={question.id} style={styles.questionBlock}>
            <Text style={styles.questionLabel}>
              Question {index + 1}
            </Text>
            <Text style={styles.questionText}>
              {question.question}
            </Text>
            <TextInput
              style={[
                styles.answerInput,
                question.answer.trim() && styles.answerInputFilled
              ]}
              placeholder="Your answer..."
              value={question.answer}
              onChangeText={(text) => handleAnswerChange(question.id, text)}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              editable={!disabled && !submitting}
            />
          </View>
        ))}
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <AlertCircle size={16} color="#DC2626" />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <Pressable
        style={[
          styles.submitButton,
          (!allAnswered || submitting || disabled) && styles.submitButtonDisabled
        ]}
        onPress={handleSubmit}
        disabled={!allAnswered || submitting || disabled}
      >
        {submitting ? (
          <>
            <ActivityIndicator size="small" color="#FFFFFF" />
            <Text style={styles.submitButtonText}>Processing...</Text>
          </>
        ) : (
          <>
            <CheckCircle2 size={16} color="#FFFFFF" />
            <Text style={styles.submitButtonText}>Submit Answers</Text>
          </>
        )}
      </Pressable>

      <Text style={styles.answerCount}>
        {answers.filter(q => q.answer.trim()).length} of {answers.length} questions answered
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    backgroundColor: '#EFF6FF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  headerText: {
    flex: 1,
    fontSize: 14,
    color: '#1E40AF',
    lineHeight: 20,
  },
  questionsContainer: {
    gap: 20,
  },
  questionBlock: {
    gap: 8,
  },
  questionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  questionText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#111827',
    lineHeight: 22,
  },
  answerInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#FFFFFF',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  answerInputFilled: {
    borderColor: '#2563EB',
    backgroundColor: '#F9FAFB',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCA5A5',
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: '#DC2626',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2563EB',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#2563EB',
    ...shadows.sm,
  },
  submitButtonDisabled: {
    backgroundColor: '#9CA3AF',
    borderColor: '#9CA3AF',
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  answerCount: {
    fontSize: 13,
    color: '#6B7280',
    textAlign: 'center',
  },
});
